/**
 * Fellido · Startseiten-Hero
 *
 * Zwei bewegte Teile:
 *  1. Wortwechsler — tauscht ein Wort in der Ueberschrift aus. Die Breite
 *     wandert mit, sonst springt der Satz. Deshalb wird jedes Wort einmal
 *     vermessen statt eine feste Breite zu raten.
 *  2. Punkte im Bild — zeigen beim Zeigen oder Tippen eine kurze Erklaerung.
 *
 * Beides haelt still, wenn das Betriebssystem weniger Bewegung verlangt.
 */
class FHero extends HTMLElement {
  connectedCallback() {
    this.ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.initWortwechsler();
    this.initPunkte();
  }

  disconnectedCallback() {
    clearInterval(this.takt);
    document.removeEventListener('click', this.onDocClick);
  }

  /* ---------------- Wortwechsler ---------------- */

  initWortwechsler() {
    this.rot = this.querySelector('[data-f-hero-rotator]');
    if (!this.rot) return;

    this.woerter = [...this.rot.querySelectorAll('[data-f-hero-word]')];
    if (this.woerter.length < 2) {
      this.rot.style.width = 'auto';
      return;
    }

    this.index = 0;
    this.messeBreiten();
    this.zeige(0, true);

    // Bei Schriftwechsel neu vermessen — Jost laedt oft nach dem ersten Bild.
    document.fonts?.ready.then(() => {
      this.messeBreiten();
      this.setzeBreite(this.index);
    });
    window.addEventListener('resize', () => {
      this.messeBreiten();
      this.setzeBreite(this.index);
    });

    if (this.ruhig) return;

    const takt = Number(this.dataset.interval || 2600);
    this.takt = setInterval(() => {
      this.index = (this.index + 1) % this.woerter.length;
      this.zeige(this.index);
    }, takt);
  }

  messeBreiten() {
    // inset:0 aus der Klasse zieht jedes wartende Wort auf Containerbreite —
    // dann misst man den Container, nicht das Wort. Beim Messen also
    // ausdruecklich loesen und die Breite vom Inhalt bestimmen lassen.
    this.breiten = this.woerter.map((w) => {
      const vorher = w.style.cssText;
      w.style.cssText =
        'position:absolute;inset:auto;width:auto;max-width:none;' +
        'visibility:hidden;white-space:nowrap;transform:none;opacity:0;';
      const b = w.getBoundingClientRect().width;
      w.style.cssText = vorher;
      return Math.ceil(b) + 1; // 1px Puffer gegen Rundung beim Zuschnitt
    });
  }

  setzeBreite(i) {
    if (this.breiten?.[i]) this.rot.style.width = `${this.breiten[i]}px`;
  }

  zeige(i, sofort = false) {
    if (sofort) this.rot.style.transition = 'none';
    this.setzeBreite(i);
    this.woerter.forEach((w, n) => {
      w.dataset.state = n === i ? 'in' : n === (i - 1 + this.woerter.length) % this.woerter.length ? 'out' : 'wartet';
    });
    if (sofort) requestAnimationFrame(() => (this.rot.style.transition = ''));
  }

  /* ---------------- Punkte im Bild ---------------- */

  initPunkte() {
    this.punkte = [...this.querySelectorAll('[data-f-hero-dot]')];
    if (this.punkte.length === 0) return;

    this.punkte.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        const offen = dot.getAttribute('aria-expanded') === 'true';
        this.alleZu();
        if (!offen) dot.setAttribute('aria-expanded', 'true');
      });
      // Am Rechner reicht Zeigen; auf dem Handy gibt es kein Zeigen.
      dot.addEventListener('mouseenter', () => {
        if (matchMedia('(hover: hover)').matches) dot.setAttribute('aria-expanded', 'true');
      });
      dot.addEventListener('mouseleave', () => {
        if (matchMedia('(hover: hover)').matches) dot.setAttribute('aria-expanded', 'false');
      });
      dot.addEventListener('focus', () => dot.setAttribute('aria-expanded', 'true'));
      dot.addEventListener('blur', () => dot.setAttribute('aria-expanded', 'false'));
    });

    document.addEventListener('click', this.onDocClick);
  }

  onDocClick = () => this.alleZu();

  alleZu() {
    this.punkte.forEach((d) => d.setAttribute('aria-expanded', 'false'));
  }
}

if (!customElements.get('f-hero')) {
  if (!customElements.get('f-hero')) customElements.define('f-hero', FHero);
}
